import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { useUser } from "@/providers/user";
import { MonthTarget, MonthTargetHistory, TargetComponentValue } from "@/types/user/target";
import { RefreshCcw } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Label, LabelList, PolarGrid, PolarRadiusAxis, RadialBar, RadialBarChart, ReferenceLine, ResponsiveContainer, Scatter, XAxis, YAxis } from "recharts";
import { ActionButton } from "../utils/action-button";
import { WithIcon } from "../utils/with-icon";
import { listGlobalMonthChartDays, TARGET_ZONE } from "./target-month-chart-range";
import { TargetMonthDetail } from "./target-month-detail";
import { TargetMonthToDateDetail } from "./target-month-detail-to-date";

/** Daily achievement % (Salesforce “Taux d’atteinte”): weighted current/goal for that day. Null when no goal. */
function computeDailyAchievementPct(monthTarget: MonthTarget, valuesForDay: TargetComponentValue[]): number | null {
    if (valuesForDay.length === 0) return null;

    let weightedAchievement = 0;
    let totalWeight = 0;

    for (const value of valuesForDay) {
        const componentInfo = monthTarget.components.find(c => c.id === value.target_component);
        const weight = monthTarget.system_class?.weights.find(w => w.name === componentInfo?.component_class?.name)?.weight ?? 0;
        if (weight === 0) continue;

        const goal = value.goal ?? 0;
        if (goal <= 0) continue;

        weightedAchievement += ((value.current ?? 0) / goal) * weight;
        totalWeight += weight;
    }

    if (totalWeight === 0) return null;
    return (weightedAchievement / totalWeight) * 100;
}

export function TargetMonth({ monthTarget, components, history }: { monthTarget: MonthTarget; components: TargetComponentValue[]; history?: MonthTargetHistory[] }) {
    const { user } = useUser();

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailToDateOpen, setDetailToDateOpen] = useState(false);

    const todayParis = DateTime.now().setZone(TARGET_ZONE);
    const isPastMonth = DateTime.fromISO(monthTarget.month, { zone: TARGET_ZONE }).endOf("month") < todayParis;

    // Calculate overall progress
    const progress = (monthTarget.current / (monthTarget.goal || 1)) * 100;
    const toDateProgress = (monthTarget.to_date_current / (monthTarget.to_date_goal || 1)) * 100;

    const workedDays = monthTarget.user_planning?.details
        .sort((a, b) => parseInt(a.day) - parseInt(b.day))
        .map(detail => {
            const d = DateTime.fromFormat(detail.day, "yyyy-MM-dd", { zone: TARGET_ZONE });
            const presence: boolean = detail.status === 1 && d.weekday <= 5;

            return {
                date: d.toFormat("dd/MM"),
                absence: presence ? null : 0.1
            };
        });

    const componentValuesByDate = components.reduce((acc, value) => {
        const dateKey = DateTime.fromISO(value.at_date).toFormat("yyyy-MM-dd");
        if (!acc.has(dateKey)) acc.set(dateKey, []);
        acc.get(dateKey)!.push(value);
        return acc;
    }, new Map<string, TargetComponentValue[]>());

    let maxDailyProgress = 0;
    // Global chart only: days up to today on the current month (components may still carry future goals).
    const historyData = (() => {
        if (!history) return [];

        const daysInMonth = listGlobalMonthChartDays(monthTarget.month, todayParis);

        return daysInMonth.map(date => {
            const dateKey = date.toFormat("yyyy-MM-dd");
            const workedDayEntry = workedDays?.find(wd => wd.date === date.toFormat("dd/MM"));
            const dailyProgress = computeDailyAchievementPct(monthTarget, componentValuesByDate.get(dateKey) ?? []);
            if (dailyProgress != null) {
                maxDailyProgress = Math.max(maxDailyProgress, dailyProgress);
            }

            return {
                date: date.toFormat("dd/MM"),
                dailyProgress: dailyProgress != null ? Number(dailyProgress.toFixed(2)) : null,
                absence: workedDayEntry?.absence || null
            };
        });
    })();

    const yAxisMax = Math.ceil(Math.max(maxDailyProgress, 100) + 20);
    const yAxisTicks = [40, 60, 80, 100, 120, 140, 160, 180, 200, 250, 300, 350, 400].filter(tick => tick <= yAxisMax);

    return (
        <>
            <TargetMonthDetail monthTarget={monthTarget} components={components} open={detailOpen} onOpenChange={setDetailOpen} />
            <TargetMonthToDateDetail monthTarget={monthTarget} components={components} open={detailToDateOpen} onOpenChange={setDetailToDateOpen} />
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between space-x-5">
                        <div>
                            <CardTitle>Objectif global du mois</CardTitle>
                            {monthTarget.user.id != user?.id && (
                                <CardDescription>
                                    Vue en tant que {monthTarget.user?.first_name} {monthTarget.user?.last_name}
                                </CardDescription>
                            )}
                        </div>
                        <div>
                            <ActionButton size="sm" variant="outline" action={`/objectif/mensuel/${monthTarget.id}?action=refresh`} disabled={isPastMonth}>
                                <WithIcon icon={RefreshCcw}>Rafraichir</WithIcon>
                            </ActionButton>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-10">
                        <div className="grid gap-4 lg:grid-cols-12">
                            <div
                                className={cn(
                                    !monthTarget.components
                                        .filter(c => Boolean(c.component_class))
                                        .filter(c => {
                                            const componentClass = monthTarget.system_class?.weights.find(c2 => c2.name === c?.component_class?.name);
                                            return (componentClass?.weight || 0) != 0;
                                        })
                                        .some(c => !c.component_class?.is_future_goal_known)
                                        ? "lg:col-span-2 lg:mt-6"
                                        : "lg:col-span-4 lg:mt-6"
                                )}
                            >
                                <div className="mx-auto h-[250px] w-[200px]">
                                    <h2 className="text-center font-semibold">Ma performance à date</h2>
                                    <p className="text-center text-xs font-normal">Je suis en {toDateProgress < 100 ? "retard" : "avance"}</p>

                                    <ResponsiveContainer width="100%" height="100%">
                                        <RadialBarChart
                                            data={[{ goal: toDateProgress / 100, fill: "#4D4D4D" }]}
                                            startAngle={90}
                                            endAngle={90 - (360 * toDateProgress) / 100}
                                            innerRadius={68}
                                            outerRadius={110}
                                        >
                                            <PolarGrid gridType="circle" radialLines={false} stroke="none" className="first:fill-muted last:fill-background" polarRadius={[76, 60]} />
                                            <RadialBar dataKey="goal" background cornerRadius={10} />
                                            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                                                <Label
                                                    content={({ viewBox }) => {
                                                        if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                                            return (
                                                                <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                                                                    <tspan x={viewBox.cx} y={(viewBox.cy || 0) - 5} className="fill-foreground text-3xl font-bold">
                                                                        {Math.floor(toDateProgress).toFixed(0)} %
                                                                    </tspan>

                                                                    <tspan
                                                                        x={viewBox.cx}
                                                                        y={(viewBox.cy || 0) + 20}
                                                                        className="cursor-pointer fill-foreground text-xs underline"
                                                                        onClick={() => setDetailToDateOpen(true)}
                                                                    >
                                                                        Voir le détail
                                                                    </tspan>
                                                                </text>
                                                            );
                                                        }
                                                    }}
                                                />
                                            </PolarRadiusAxis>
                                        </RadialBarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            {!monthTarget.components
                                .filter(c => Boolean(c.component_class))
                                .filter(c => {
                                    const componentClass = monthTarget.system_class?.weights.find(c2 => c2.name === c?.component_class?.name);
                                    return (componentClass?.weight || 0) != 0;
                                })
                                .some(c => !c.component_class?.is_future_goal_known) && (
                                <div className="lg:col-span-2 lg:mt-6">
                                    <div className="mx-auto h-[250px] w-[200px]">
                                        <h2 className="text-center text-sm font-semibold">Sur le mois complet</h2>
                                        <p className="text-center text-xs font-normal">Quel objectif est sécurisé ?</p>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RadialBarChart
                                                data={[{ goal: progress / 100, fill: "#3b82f6" }]}
                                                startAngle={90}
                                                endAngle={90 - (360 * progress) / 100}
                                                innerRadius={68}
                                                outerRadius={110}
                                            >
                                                <PolarGrid gridType="circle" radialLines={false} stroke="none" className="first:fill-muted last:fill-background" polarRadius={[76, 60]} />
                                                <RadialBar dataKey="goal" background cornerRadius={10} />
                                                <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                                                    <Label
                                                        content={({ viewBox }) => {
                                                            if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                                                return (
                                                                    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                                                                        <tspan x={viewBox.cx} y={(viewBox.cy || 0) - 5} className="fill-foreground text-3xl font-bold">
                                                                            {Math.floor(progress).toFixed(0)} %
                                                                        </tspan>

                                                                        <tspan
                                                                            x={viewBox.cx}
                                                                            y={(viewBox.cy || 0) + 20}
                                                                            className="cursor-pointer fill-foreground text-xs underline"
                                                                            onClick={() => setDetailOpen(true)}
                                                                        >
                                                                            Voir le détail
                                                                        </tspan>
                                                                    </text>
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </PolarRadiusAxis>
                                            </RadialBarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}
                            {historyData.length > 0 && (
                                <div className="h-[280px] w-full lg:col-span-8 lg:mt-6">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ChartContainer
                                            config={
                                                {
                                                    dailyProgress: { label: "Atteinte quotidienne", color: "#3b82f6" }
                                                } satisfies ChartConfig
                                            }
                                        >
                                            <ComposedChart data={historyData} margin={{ top: 48, right: 16, left: 0, bottom: 0 }}>
                                                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                                                <ReferenceLine y={100} stroke="#94a3b8" strokeWidth={1} isFront={false} />
                                                <YAxis
                                                    domain={[0, yAxisMax]}
                                                    ticks={yAxisTicks}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tickFormatter={value => `${value}%`}
                                                    className="text-xs"
                                                />
                                                <XAxis
                                                    type="category"
                                                    dataKey="date"
                                                    domain={
                                                        historyData.length > 0
                                                            ? [historyData[0].date, historyData[historyData.length - 1].date]
                                                            : undefined
                                                    }
                                                    tickLine={false}
                                                    axisLine={false}
                                                    className="text-xs"
                                                />
                                                <Bar name="Atteinte quotidienne de l'objectif" dataKey="dailyProgress" fill="#3b82f6" maxBarSize={28}>
                                                    <LabelList
                                                        dataKey="dailyProgress"
                                                        content={({ x, y, width, value }) => {
                                                            if (value == null || x == null || y == null || width == null) return null;
                                                            const cx = Number(x) + Number(width) / 2;
                                                            const cy = Number(y) - 6;
                                                            const label = `${Number(value).toLocaleString("fr-FR", {
                                                                minimumFractionDigits: 1,
                                                                maximumFractionDigits: 1
                                                            })}%`;
                                                            return (
                                                                <text
                                                                    x={cx}
                                                                    y={cy}
                                                                    fill="#334155"
                                                                    fontSize={10}
                                                                    fontWeight={500}
                                                                    textAnchor="start"
                                                                    dominantBaseline="middle"
                                                                    transform={`rotate(-90, ${cx}, ${cy})`}
                                                                >
                                                                    {label}
                                                                </text>
                                                            );
                                                        }}
                                                    />
                                                </Bar>
                                                {workedDays && <Scatter name="Jours non travaillés" dataKey="absence" fill="#94a3b8" shape="circle" strokeWidth={1} />}
                                            </ComposedChart>
                                        </ChartContainer>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>
                    {monthTarget.last_computed_at && (
                        <div className="text-right text-xs text-muted-foreground">Dernière mise à jour: {DateTime.fromISO(monthTarget.last_computed_at).toLocaleString(DateTime.DATETIME_MED)}</div>
                    )}
                </CardContent>
            </Card>
        </>
    );
}
